import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  makeStyles,
  createStyles,
  Theme,
  Tooltip,
} from "@material-ui/core";
import HomeCard from "../HomeCard";
import HomeCardMobile from "../HomeCardMobile";
import InstallationModel from "../../core/models/Installation";
import clsx from "clsx";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CustomerModel from "../../core/models/Customer";
import {
  EVENT_HOMEPAGE_CLOSE_ALL,
  EVENT_HOME_PAGE_PRODUCT_FILTER_CHANGE,
  EVENT_HOME_PAGE_STATUS_FILTER_CHANGE,
  EVENT_HOME_PAGE_SMART_FILTER_CHANGE,
  EVENT_HOMEPAGE_OPEN_ALL,
  EVENT_HOME_PAGE_UF_FILTER_CHANGE,
  EVENT_HOME_PAGE_TRIP_FILTER_CHANGE,
  EVENT_LANDING_PAGE_FILTER,
} from "../../Events";
import { useHubx, useHubXSubscribe } from "../../HubX";
import HomeTable from "../HomeTable";
import { useHubXValue } from "../../core/hooks/useHubHooks";
import AllInstallationsFilter from "../../core/models/AllInstallationsFilter";
import { installationLevelStatus } from "../../core/models/LevelStatus";
import InstallationGroup from "../../core/models/InstallationGroup";
import { useSnackbar } from "notistack";
import InfoIcon from "@material-ui/icons/Help";
import {
  FAVORITES,
  TOOLTIP_FILTER_ALL_INSTALLATIONS,
} from "../../core/constants/tooltipHelpersText";
import { FILTER_ALL_INSTALLATIONS_NEW } from "../../Fedx";
import { tripStatus } from "../../core/models/TripStatus";
import { IInstallation } from "../../core/contracts/IInstallation";
import useConstructor from "./../../core/hooks/useConstructor";
import { includes } from "lodash";
import { OrderOptions } from "../../core/contracts/APIInstallationFilter";
import { DEFAULT_RELOAD_MINUTES } from "../../core/constants/appGlobals";
import { installationOrderType, orderType } from '../../core/models/OrderType';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "margin": theme.spacing(2, 0),

      "& .MuiAccordionDetails-root": {
        padding: theme.spacing(0, 0, 1, 0),
      },
      "& > .MuiAccordionSummary-root": {
        fontSize: "1rem",
        background: theme.customPaper.background.cardBackgroundLight,
        maxHeight: "48px",
        minHeight: "48px",
        // color: theme.palette.common.white
        fontWeight: "bold",
      },

      "& > .Mui-expanded": {
        "background": theme.customPaper.background.cardBackground,
        "color": theme.palette.common.white,
        "fontWeight": "bold",

        "& .icon": {
          color: theme.palette.common.white,
        },
        "borderBottom": theme.palette.type == "dark" ? "2px solid #565555 " : "transparent",
      },
    },

    cardWrapper: {
      display: "flex",
      flexWrap: "wrap",
      width: "100%",
      padding: theme.spacing(2),
      columnGap: theme.spacing(4),
      rowGap: `${theme.spacing(3)}px`,
      [theme.breakpoints.between("sm", "md")]: {
        justifyContent: "center",
      },
      [theme.breakpoints.down("xs")]: {
        padding: 0,
        flex: 1,
        flexWrap: "unset",
        columnGap: "unset",
        rowGap: `${theme.spacing(1)}px`,
        flexDirection: "column",
      },
    },

    tableWrapper: {
      display: "flex",
      flexDirection: "column",
      flexWrap: "wrap",
      flex: "1 1",
    },

    hidden: {
      display: "none",
    },

    infoIconExpanded: {
      marginLeft: theme.spacing(1),
      color: theme.palette.common.white,
    },

    infoIcon: {
      marginLeft: theme.spacing(1),
      color: "inherit",
    },
  })
);

type HomePageViewType = "card" | "table" | "mobile-card";

type HomeGroupAccordionProps = {
  customer: CustomerModel;
  radixGroup?: InstallationGroup;
  selectedView: HomePageViewType;
  startOpen?: boolean;
  onExpand?: () => void;
  onExpandAll?: () => void;
  onClose?: () => void;
  onChangeOrder?: (
    customerId: string | undefined,
    idx: any,
    isReverse: boolean | undefined
  ) => void;
  disableClick?: boolean;
  favoriteGroup?: boolean;
  hasFavorites?: boolean;
  favoritesTooltip?: boolean;
  alwaysExpanded?: boolean;
  onLandingPage?: boolean;
  orderOptions?: OrderOptions;
  handleFavorite?: (id: string, favoriteOption: boolean) => void;
};

function HomeGroupAccordion({
  customer,
  selectedView,
  startOpen,
  onExpand,
  onClose,
  disableClick = false,
  favoriteGroup,
  radixGroup,
  hasFavorites,
  favoritesTooltip,
  alwaysExpanded,
  onChangeOrder,
  onLandingPage = false,
  handleFavorite,
  orderOptions,
}: HomeGroupAccordionProps) {
  const toastr = useSnackbar();
  const hub = useHubx();
  const classes = useStyles();

  const [expanded, setExpanded] = React.useState(() => {
    return startOpen || false;
  });

  useHubXSubscribe(EVENT_HOMEPAGE_CLOSE_ALL, () => setExpanded(false));
  useHubXSubscribe(EVENT_HOMEPAGE_OPEN_ALL, () => setExpanded(true));

  const tableView = selectedView === "table";
  const simpleCardMobile = selectedView === "mobile-card";
  const productFilter = (useHubXValue(
    EVENT_HOME_PAGE_PRODUCT_FILTER_CHANGE,
    ""
  ) as unknown) as string[];

  const statusFilter = (useHubXValue(
    EVENT_HOME_PAGE_STATUS_FILTER_CHANGE,
    ""
  ) as unknown) as string[];

  const orderType = (useHubXValue(
    EVENT_HOME_PAGE_STATUS_FILTER_CHANGE,
    ""
  ) as unknown) as string[];

  let smartFilter = useHubXValue(EVENT_HOME_PAGE_SMART_FILTER_CHANGE, "");

  const ufFilter = (useHubXValue(EVENT_HOME_PAGE_UF_FILTER_CHANGE, "") as unknown) as string[];

  const tripFilter = (useHubXValue(EVENT_HOME_PAGE_TRIP_FILTER_CHANGE, "") as unknown) as string[];

  if (smartFilter) {
    smartFilter = smartFilter.toLocaleLowerCase();
  }

  const filterAction = async () => {
    if (expanded) {
      const eventParams: AllInstallationsFilter = {
        installation_name_id: smartFilter,
        installation_product: productFilter == ["all"] ? undefined : (productFilter as string[]),
        installation_alert:
          statusFilter == ["all"] ? undefined : (statusFilter as installationLevelStatus[]),
        installation_trip_status:
          tripFilter == ["all"] ? undefined : (tripFilter as tripStatus[]),
        installation_uf: ufFilter == ["all"] ? undefined : (ufFilter as string[]),
        return_ids: false,
        customer_id: customer.customer_id,
       installation_orderType: orderType == ["all"] ? undefined : (orderType as installationOrderType[]), 
      };
      hub.notify(EVENT_LANDING_PAGE_FILTER, eventParams);
    }
  };

  React.useMemo(() => {
    filterAction();
  }, [productFilter, statusFilter, smartFilter, tripFilter, ufFilter, expanded]);

  const MINUTE_MS = 60000;

  React.useEffect(() => {
    if(expanded){
      const interval = setInterval(() => {
        filterAction();
      }, DEFAULT_RELOAD_MINUTES * MINUTE_MS);
    
      return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    }
  }, [expanded]);

  return (
    <Accordion
      className={clsx(classes.root, tableView || simpleCardMobile)}
      elevation={0}
      expanded={alwaysExpanded ?? expanded}
      onChange={() =>
        setExpanded((prev) => {
          if (!prev) {
            if (onExpand) onExpand();
          } else {
            if (onClose) onClose();
          }
          return !prev;
        })
      }
      // TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={alwaysExpanded ? "" : <ExpandMoreIcon fontSize="large" className={"icon"} />}
      >
        {customer.customer_name}
        {favoritesTooltip ||
        (!favoritesTooltip && customer.customer_name != "Favoritos" && !onLandingPage) ? (
          <Tooltip
            title={favoritesTooltip ? FAVORITES : TOOLTIP_FILTER_ALL_INSTALLATIONS}
            placement="top"
          >
            <InfoIcon
              className={expanded ? classes.infoIconExpanded : classes.infoIcon}
              fontSize="small"
              color="primary"
            />
          </Tooltip>
        ) : (
          ""
        )}
      </AccordionSummary>

      <AccordionDetails>
        <div
          className={clsx(classes.cardWrapper, (tableView || simpleCardMobile) && classes.hidden)}
        >
          {customer?.installations?.map((installation: InstallationModel) => {
            const radixInstallation = radixGroup?.installationList.find(
              (e) => e.installationId === installation.installation_id
            );

            return (
              <HomeCard
                key={installation.installation_id}
                customerId={customer?.customer_id}
                installation={installation}
                radixInstallation={radixInstallation}
                radixGroup={radixGroup}
                favoriteGroup={favoriteGroup}
              />
            );
          })}
        </div>

        <div
          className={clsx(classes.cardWrapper, (tableView || !simpleCardMobile) && classes.hidden)}
        >
          {customer?.installations?.map((installation: InstallationModel) => (
            <HomeCardMobile
              key={installation.installation_id}
              customerId={customer?.customer_id}
              installation={installation}
              favoriteGroup={favoriteGroup}
              hasFavorites={hasFavorites}
              handleFavorite={handleFavorite}
            />
          ))}
        </div>

        <div
          className={clsx(classes.tableWrapper, (!tableView || simpleCardMobile) && classes.hidden)}
        >
          <HomeTable
            expanded={expanded}
            disableClick={disableClick}
            onChange={() =>
              setExpanded((prev) => {
                if (!prev) {
                  if (onExpand) onExpand();
                } else {
                  if (onClose) onClose();
                }
                return !prev;
              })
            }
            favoriteGroup={favoriteGroup}
            hasFavorites={hasFavorites}
            handleFavorite={handleFavorite}
            installations={customer?.installations}
            customerId={customer.customer_id}
            onChangeOrder={onChangeOrder}
            orderOptions={orderOptions}
            onLandingPage={onLandingPage}
          />
        </div>
      </AccordionDetails>
    </Accordion>
  );
}

export default HomeGroupAccordion;
